import { useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { Center, Text3D, MeshDistortMaterial } from "@react-three/drei";
import { useSpring, animated } from '@react-spring/three';
import { 
  landscapeLobbyThirdSectionSelectionAtom, 
  audioVolumeAtom, 
  languageAtom 
} from "../../GlobalState.jsx";
import useSoundEffectsPlayer from "../../soundPlayers/useSoundEffectsPlayer.jsx";
import MeshColors from "../MeshColors.jsx";
import QrCode3d from "../QRCode3D.jsx";
import LobbySettings from "../LobbySettings.jsx";
import translations from "../../dictionaries/translations.js";
import { trackEvent } from "../../api";

export default function ThirdSection({ position }) {
  const [display, setDisplay] = useAtom(landscapeLobbyThirdSectionSelectionAtom)
  const language = useAtomValue(languageAtom)

  function InviteFriendsButton() {
    const [hover, setHover] = useState(false)
    const audioVolume = useAtomValue(audioVolumeAtom)
    function handlePointerEnter(e) {
      e.stopPropagation()
      document.body.style.cursor = 'pointer'
      setHover(true)
    }
    function handlePointerLeave(e) {
      e.stopPropagation()
      document.body.style.cursor = 'default'
      setHover(false)
    }
    function handlePointerDown(e) {
      e.stopPropagation()
      setDisplay('invite')
      playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
    }
    return <group name='invite-friends-button' position={[7.5, 0, -5.6]} scale={0.9}>
      <mesh name='background-outer' scale={[4.2, 0.01, 0.75]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={ (hover || display === 'invite') ? 'green' : 'orange' }/>
      </mesh> 
      <mesh name='background-inner' scale={[4.15, 0.02, 0.7]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={MeshColors.spaceDark}/>
      </mesh>
      <mesh 
      name='wrapper' 
      scale={[4.2, 0.02, 0.75]}
      onPointerEnter={e => handlePointerEnter(e)}
      onPointerLeave={e => handlePointerLeave(e)}
      onPointerDown={e => handlePointerDown(e)}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='orange' transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.02, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          size={0.4}
          height={0.01}
        >
          {translations.lobby.inviteFriends[language]}
          <meshStandardMaterial color={ (hover || display === 'invite') ? 'green' : 'orange' }/>
        </Text3D>
      </Center>
    </group>
  }
  function SettingsButtonLobby() {
    const [hover, setHover] = useState(false)
    const audioVolume = useAtomValue(audioVolumeAtom)
    function handlePointerEnter(e) {
      e.stopPropagation()
      document.body.style.cursor = 'pointer'
      setHover(true)
    }
    function handlePointerLeave(e) {
      e.stopPropagation()
      document.body.style.cursor = 'default'
      setHover(false)
    }
    function handlePointerDown(e) {
      e.stopPropagation()
      setDisplay('settings')
      trackEvent({ surface: 'lobby', action: 'open_settings', category: 'ui' })
      playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
    }
    return <group name='settings-button' position={[10.8, 0, -5.6]} scale={0.9}>
      <mesh name='background-outer' scale={[2.8, 0.01, 0.75]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={ (hover || display === 'settings') ? 'green' : 'orange' }/>
      </mesh> 
      <mesh name='background-inner' scale={[2.75, 0.02, 0.7]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={MeshColors.spaceDark}/>
      </mesh>
      <mesh 
      name='wrapper' 
      scale={[2.8, 0.02, 0.75]}
      onPointerEnter={e => handlePointerEnter(e)}
      onPointerLeave={e => handlePointerLeave(e)}
      onPointerDown={e => handlePointerDown(e)}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='black' transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.02, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          size={0.4}
          height={0.01}
        >
          {translations.lobby.settings[language]}
          <meshStandardMaterial color={ (hover || display === 'settings') ? 'green' : 'orange' }/>
        </Text3D>
      </Center>
    </group>
  }
  function InviteFriends() {
    function CopyLinkButton({ position }) {
      const [hover, setHover] = useState(false)
      const audioVolume = useAtomValue(audioVolumeAtom)
      function handlePointerEnter(e) {
        e.stopPropagation()
        document.body.style.cursor = 'pointer'
        setHover(true)
      }
      function handlePointerLeave(e) {
        e.stopPropagation()
        document.body.style.cursor = 'default'
        setHover(false)
      }
      function copyURLToClipboard() {
        const url = window.location.href;
      
        if (navigator.clipboard && navigator.clipboard.writeText) {
          // Modern browsers with Clipboard API support
          navigator.clipboard.writeText(url)
            .then(() => {
            })
            .catch(err => {
              console.error("Failed to copy URL: ", err);
            });
        } else {
          // Fallback for older browsers
          const tempInput = document.createElement("input");
          document.body.appendChild(tempInput);
          tempInput.value = url;
          tempInput.select();
          document.execCommand("copy");
          document.body.removeChild(tempInput);
        }
      }
      const AnimatedMeshDistortMaterial = animated(MeshDistortMaterial)
      const [springs, api] = useSpring(() => ({        
        from: {
          opacity: 0, 
        }
      }))
      function handlePointerDown(e) {
        e.stopPropagation()
        copyURLToClipboard()
        api.start({
          from: {
            opacity: 1
          },
          to: [
            {
              opacity: 1
            },
            { 
              opacity: 0,
              delay: 500,
              config: {
                tension: 170,
                friction: 26
              }
            }
          ]
        })
        playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
      }
      return <group name='copy-link-button' position={position}>
        <mesh name='background-outer' scale={[3, 0.01, 0.75]}>
          <boxGeometry args={[1, 1, 1]}/>
          <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
        </mesh> 
        <mesh name='background-inner' scale={[2.95, 0.02, 0.7]}>
          <boxGeometry args={[1, 1, 1]}/>
          <meshStandardMaterial color={MeshColors.spaceDark}/>
        </mesh>
        <mesh 
        name='wrapper' 
        scale={[3, 0.02, 0.75]}
        onPointerEnter={e => handlePointerEnter(e)}
        onPointerLeave={e => handlePointerLeave(e)}
        onPointerDown={e => handlePointerDown(e)}>
          <boxGeometry args={[1, 1, 1]}/>
          <meshStandardMaterial color='orange' transparent opacity={0}/>
        </mesh>
        <Center position={[0, 0.02, 0]} rotation={[-Math.PI/2, 0, 0]}>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            size={0.4}
            height={0.01}
          >
            {translations.lobby.copyLink[language]}
            <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
          </Text3D>
        </Center>
        <Text3D 
          name='copied-tooltip'
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-1,0,-0.6]}
          rotation={[-Math.PI/2, 0, 0]}
          size={0.4}
          height={0.01}
        >
          {translations.lobby.copied[language]}
          <AnimatedMeshDistortMaterial
            speed={5}
            distort={0}
            color='limegreen'
            transparent
            opacity={springs.opacity}
          />
        </Text3D>
      </group>
    }

    // get image before download
    // display it as drei-image
    return <group>
      <QrCode3d text={window.location.href} position={[8.8,0.02,-2]} scale={0.8} rotation={[-Math.PI/2,0,0]}/>
      <Text3D
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={[6.1,0,1.5]}
      rotation={[-Math.PI/2,0,0]}
      size={0.35}
      height={0.01}
      lineHeight={0.9}>
        {translations.lobby.scanQrCode[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
      <CopyLinkButton position={[8.6, 0.02, 5]}/>
    </group>
  }

  const { playSoundEffect } = useSoundEffectsPlayer()

  return <group name='third-section' position={position}>
    {/* <mesh name='background-panel' position={[8.8, 0, 0]}>
      <boxGeometry args={[7.4, 0.01, 14]}/>
      <meshStandardMaterial color='black' transparent opacity={0.5}/>
    </mesh> */}
    <InviteFriendsButton/>
    { display === 'invite' && <InviteFriends/> }
    <SettingsButtonLobby/>
    { display === 'settings' && <>
    <LobbySettings position={[2,0,-1]} scale={0.8}/>
    </> }
  </group>
}
